import { COMP_REPEATER, CONNECTOR, IC, RLC_TYPES } from "../../PCBHelper/index.js";
import _ from 'lodash';

class PathDecomposer {
  constructor(data, comps, powerNets, visibleSignals) {
    this.data = data;
    this.comps = comps;
    this.powerNets = powerNets;
    this.decomposedPaths = null;
    this.nodesScale = {};
    this.groupActualLength = {};//[root]:{[groupPath]:length}
    this.groupLayoutLength = {};//[root]:{[groupPath]:length}
    this.signalGroupPath = {};//[root]:[{signal, groupPath}]
    this.parallelRLCs = [];
    this.parallelComps = [];
    this.parallelAllComps = [];
    this.endParallelComps = {};
    this.groupNodeLayer = {};//[root]:{[node]:layer}
    this.splitChipComps = {}//[root]:[]
    this.chipCompsPrevList = {}//[root]:{[comp]:[...prevComps]}
    this.sameSegments = {}
    this.visibleSignals = visibleSignals || [];
    this.endCompPinList = {}
    this.startCompPinList = {}
  }

  initComps = () => {
    this.parallelRLCs = [];
    this.seriesRLCs = [];
    this.repeaterComps = [];
    this.ICComps = [];
    this.compWeights = {};
    this.parallelComps = []
    this.parallelAllComps = []
    this.compMap = new Map();
    for (let comp of this.comps) {
      this.compMap.set(comp.name, comp);
      if (RLC_TYPES.includes(comp.type) && comp.pins && (comp.pins.find(pin => this.powerNets.includes(pin.net)) || comp.pins.length === 1)) {
        this.parallelRLCs.push(comp.name);
        this.compWeights[comp.name] = 0;
        continue;
      }
      if (RLC_TYPES.includes(comp.type) && comp.pins && comp.pins.length === 2 && !comp.pins.find(pin => !pin.signal)) {
        this.seriesRLCs.push(comp.name);
        this.compWeights[comp.name] = 1;
        continue;
      }
      if (comp.type === COMP_REPEATER || (RLC_TYPES.includes(comp.type) && comp.pins && comp.pins.length > 2 && !comp.pins.find(pin => !pin.signal))) {
        this.repeaterComps.push(comp.name);
        this.compWeights[comp.name] = comp.pins.length / 2;
        continue;
      }

      if (![COMP_REPEATER, IC, CONNECTOR].includes(comp.type) && comp.pins && (comp.pins.find(pin => this.powerNets.includes(pin.net)) || comp.pins.length === 1)) {
        this.parallelComps.push(comp.name);
        this.compWeights[comp.name] = 0;
        continue;
      }

      this.ICComps.push(comp.name);
      this.compWeights[comp.name] = comp.pins.length;

    }
    this.parallelAllComps = [...this.parallelRLCs, ...this.parallelComps];
  }

  //get a map of node segments
  initNodeSegmentMap = () => {
    this.nodeSegments = {};
    this.segments = [];
    this.endCompPinList = {}

    for (let path of this.signalPaths || []) {
      const root = path.root;
      if (!this.nodeSegments[root]) {
        this.nodeSegments[root] = new Map()
      }
      if (!this.endCompPinList[root]) {
        this.endCompPinList[root] = new Map()
      }
      if (!this.startCompPinList[root]) {
        this.startCompPinList[root] = new Map()
      }
      for (let segment of path.segments || []) {
        if (this.visibleSignals.length && !this.visibleSignals.includes(path.signal)) continue
        const key = `${path.signal}::${path.name}::${segment.from}::${segment.to}`;
        const segmentItem = {
          from: segment.from,
          to: segment.to,
          length: segment.length,
          net: segment.net,
          signal: path.signal,
          pathName: path.name,
          startPin: segment.startPin,
          endPin: segment.endPin
        };
        if (segment.endPin && segment.to !== segment.from) {
          let toCompPins = this.endCompPinList[root].get(segment.to) || [];
          toCompPins = [...new Set([...toCompPins, segment.endPin])];
          this.endCompPinList[root].set(segment.to, toCompPins)
        }
        if (segment.startPin && segment.to !== segment.from) {
          let startCompPins = this.startCompPinList[root].get(segment.from) || [];
          startCompPins = [...new Set([...startCompPins, segment.startPin])];
          this.startCompPinList[root].set(segment.from, startCompPins)
        }

        // If first time seeing this (signal,from,to) keep it in nodeSegments as primary
        if (!this.nodeSegments[root].has(key)) {
          this.nodeSegments[root].set(key, { ...segmentItem });
          this.segments.push(segmentItem);
        } else {
          // multiple physical segments exist between same from/to (different pins)
          // collect them in sameSegments so layout can handle multiple pins
          if (!this.sameSegments[root]) this.sameSegments[root] = new Map();
          const arr = this.sameSegments[root].get(key) || [];
          // if this is the first duplicate, include the original stored segment too
          if (arr.length === 0) {
            const orig = this.nodeSegments[root].get(key);
            if (orig) arr.push({ ...orig });
          }
          arr.push({ ...segmentItem });
          this.sameSegments[root].set(key, arr);
          // also record in segments list for completeness
          this.segments.push(segmentItem);
        }
      }
    }
  }

  // compose decomposed paths
  decompose = () => {
    this.initComps()
    this.signalPaths = []// new Map();
    this.rootPathObj = {};
    this.signalNodes = {}
    for (let item of this.data.branch) {
      if (this.visibleSignals.length && !this.visibleSignals.includes(item.signal)) continue
      const paths = this.decomposeSignal(item.signal, item.path);
      this.signalPaths.push(...paths);
      /*  this.signalPaths.set(item.signal, paths); */
      this.signalNodes[item.signal] = paths.map(path => path.nodes);
    }
    this.initNodeSegmentMap()
    this.updateGroupPathLength();

    this.decomposedPaths = {
      pcbId: this.data.pcbId,
      lengthUnit: this.data.lengthUnit,
      signalPaths: this.signalPaths,
      rootPathObj: this.rootPathObj,
      nodeSegments: this.nodeSegments,
      groupActualLength: this.groupActualLength,
      nodeLength: this.nodeLength,
      groupLayoutLength: this.groupLayoutLength,
      parallelPrvComps: this.parallelPrvComps || [],
      endParallelComps: this.endParallelComps || {},
      splitChipComps: this.splitChipComps,
      chipCompsPrevList: this.chipCompsPrevList,
      sameSegments: this.sameSegments,
      endCompPinList: this.endCompPinList,
      startCompPinList: this.startCompPinList
    };

    return this.decomposedPaths;
  }

  updateGroupPathLength = () => {
    this.nodeLength = {};
    this.nodeActualScaleLength = {}
    this.groupLayoutLength = {}
    this.parallelPrvComps = {}

    for (let root of this.data.root) {
      if (!this.nodeLength[root]) {
        this.nodeLength[root] = new Map()
      }
      if (!this.groupLayoutLength[root]) {
        this.groupLayoutLength[root] = new Map()
      }
      if (!this.nodeActualScaleLength[root]) {
        this.nodeActualScaleLength[root] = new Map()
      }
      if (!this.parallelPrvComps[root]) {
        this.parallelPrvComps[root] = []
      }
      if (!this.groupActualLength[root]) {
        continue;
      }

      const endParallelCompList = this.endParallelComps[root] || [];
      const rootGroupNodes = [...this.rootPathObj[root].groupNodes];
      const splitChipComps = this.splitChipComps[root] || [];
      const rootSplitCompIndex = rootGroupNodes.findIndex(comp => splitChipComps.includes(comp));
      const rootNodes = rootSplitCompIndex === -1 ? [...rootGroupNodes] : rootGroupNodes.slice(0, rootSplitCompIndex + 1);
      let lengthList = [...this.rootPathObj[root].groupPath.map(item => (`${item[0]}::${item[item.length - 1]}`))]
      let existGroupPaths = []
      for (let rootGroupNode of rootGroupNodes.reverse()) {
        //filter same segments
        const filterGroups = this.signalGroupPath[root].filter(item =>
          item.groupPath && item.groupPath[0] === rootGroupNode
          && !existGroupPaths.includes(`${item.signal}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`));
        lengthList.push(
          ...filterGroups.map(item => (`${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`))
        )
        existGroupPaths.push(...filterGroups.map(item => `${item.signal}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`))
      }
      const filterGroups = this.signalGroupPath[root].filter(item => !existGroupPaths.includes(`${item.signal}::${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`));
      lengthList.push(
        ...filterGroups.map(item => (`${item.groupPath[0]}::${item.groupPath[item.groupPath.length - 1]}`))
      )

      let singleLength = 3, totalNodeGroups = [], totalParallelCompsNodes = {}, allNodeLengthObj = {}, parallelPrevNode = {};
      lengthList = [...new Set(lengthList)];
      let currentLength = lengthList.length === 1 ? 9 : 4;
      for (let groupName of lengthList) {
        const [start, end] = groupName.split('::');
        const filterPaths = this.signalGroupPath[root].filter(item => item.groupPath[0] === start && item.groupPath[item.groupPath.length - 1] === end);
        // let maxNodeWidth = 0;
        for (let path of filterPaths) {
          // let seriesNodeWidth = 0
          /*  const filterNodes = path.groupPath.filter((node, index) => index > 0 && index < path.groupPath.length - 1); */
          /*   const nodeWidth = filterNodes.map(node => {
              const width = this.getNodeWidth(node);
              return width
            }).reduce((a, b) => a + b, 1); */
          /*  if (maxNodeWidth < nodeWidth) {
             maxNodeWidth = nodeWidth;
           } */
          //find all parallel rlc nodes
          const startSegment = path.segments.find(edge => edge.from === path.groupPath[0] && edge.to === path.groupPath[1]) || {};
          const findParallelSegment = this.segments.find(item => item.from === path.groupPath[0] && (!item.startPin || item.startPin === startSegment.startPin) && endParallelCompList.includes(item.to))
          if (findParallelSegment) {
            this.parallelPrvComps[root].push(path.groupPath[0])
          }
          const endSegment = path.segments.find(edge => edge.from === path.groupPath[path.groupPath.length - 2] && edge.to === path.groupPath[path.groupPath.length - 1]) || {};

          const findEndParallelSegment = this.segments.find(item => item.from === path.groupPath[path.groupPath.length - 1] && (!item.startPin || item.startPin === endSegment.endPin) && endParallelCompList.includes(item.to))
          if (findEndParallelSegment) {
            this.parallelPrvComps[root].push(path.groupPath[path.groupPath.length - 1])
          }
        }
        // currentLength = Math.max(currentLength, maxNodeWidth);
        let parallelCompsNodeLength = []
        for (let path of filterPaths) {
          //re set current length by exist node
          let prevLength = 0;
          let prevSegment = null;
          for (let j = 1; j < path.groupPath.length - 1; j++) {

            const node = path.groupPath[j];
            const prevNode = path.groupPath[j - 1];
            const segment = path.segments.find(edge => edge.from === path.groupPath[j - 1] && edge.to === node) || {};
            if (!segment) {
              continue;
            }
            prevLength += this.seriesRLCs.includes(prevNode) && prevSegment.endPin !== segment.startPin ? 2 : 0;

            //find this node of connect parallel RLC
            let findParallelSegmentBySeries = null;
            let findParallelSegment = this.segments.find(item => item.from === node && (!item.startPin || item.startPin === segment.endPin) && endParallelCompList.includes(item.to));
            if (!findParallelSegment && this.seriesRLCs.includes(node)) {
              const comp = this.compMap.get(node) || {};
              const pin = comp.pins.find(item => item.pin !== segment.endPin && item.signal === path.signal) || {};
              findParallelSegmentBySeries = this.segments.find(item => item.from === node && (!item.startPin || item.startPin === pin.pin) && endParallelCompList.includes(item.to));
            }
            const isParallelRLC = this.parallelAllComps.includes(node) && !endParallelCompList.includes(node);
            let spacing = (isParallelRLC || findParallelSegment || findParallelSegmentBySeries || !this.compMap.get(node))
              || (parallelCompsNodeLength.find(it => it.node === prevNode) || !this.compMap.get(prevNode)) ? 1 : singleLength;

            let nodeLength = prevLength + spacing;
            nodeLength = findParallelSegmentBySeries ? nodeLength + 2 : nodeLength;
            prevLength = nodeLength;

            if (isParallelRLC || findParallelSegment || findParallelSegmentBySeries) {
              parallelCompsNodeLength.push({ node, nodeLength, groupNodes: [...path.groupPath] });
              parallelPrevNode[node] = prevNode;
            }
            if (findParallelSegment || findParallelSegmentBySeries) {
              this.parallelPrvComps[root].push(node)
            }
            prevSegment = { ...segment }
          }
        }

        let _parallelCompsNodeLength = JSON.parse(JSON.stringify(parallelCompsNodeLength)).sort((a, b) => a.nodeLength - b.nodeLength);
        let prevParallelNode = null, parallelCompsNodes = {};
        let nodeGroups = [], currNodeGroup = [], currNodeGroupNodes = {}
        for (let index = 0; index < _parallelCompsNodeLength.length; index++) {
          let nodeItem = _parallelCompsNodeLength[index];
          let nodeLength = nodeItem.nodeLength;
          if (totalParallelCompsNodes[nodeItem.node]) {
            parallelCompsNodes[nodeItem.node] = { ...totalParallelCompsNodes[nodeItem.node], actualLength: nodeLength };
            prevParallelNode = { node: nodeItem.node, nodeLength: totalParallelCompsNodes[nodeItem.node].nodeLength }
            continue;
          }

          let spacing = 0;

          if (!prevParallelNode) {
            // eslint-disable-next-line no-loop-func
            const findExistNodeList = _parallelCompsNodeLength.filter((it, i) => i > index && !!totalParallelCompsNodes[it.node] && Math.abs(nodeLength - it.nodeLength) < 1);
            if (findExistNodeList.length) {
              const lastNode = findExistNodeList[findExistNodeList.length - 1];
              let lastNodeLength = lastNode.nodeLength;
              const findSameGroup = totalNodeGroups.find(it => it.group.includes(lastNode.node));
              if (findSameGroup) {
                const lastNode = findSameGroup.group[findSameGroup.group.length - 1];
                lastNodeLength = totalParallelCompsNodes[lastNode].nodeLength;
              }
              spacing = nodeLength >= lastNodeLength ? 1 - (nodeLength - lastNodeLength) : 1;
              nodeLength = lastNodeLength + spacing;
            }
            parallelCompsNodes[nodeItem.node] = { ...nodeItem, actualLength: nodeItem.nodeLength };
            prevParallelNode = { node: nodeItem.node, nodeLength }
            continue;
          }

          if (nodeLength < prevParallelNode.nodeLength || nodeLength - prevParallelNode.nodeLength < 1) {
            spacing = nodeLength >= prevParallelNode.nodeLength ? 1 - Math.abs(nodeLength - prevParallelNode.nodeLength) : (prevParallelNode.nodeLength + 1) - nodeLength;
            nodeLength += spacing;
            const findExistNodeList = _parallelCompsNodeLength.filter((it, i) => i > index && totalParallelCompsNodes[it.node] && Math.abs(nodeLength - it.nodeLength) < 1);
            if (findExistNodeList.length) {
              const lastNode = findExistNodeList[findExistNodeList.length - 1];
              let lastNodeLength = lastNode.nodeLength;
              const findSameGroup = totalNodeGroups.find(it => it.group.includes(lastNode.node));
              if (findSameGroup) {
                const lastNode = findSameGroup.group[findSameGroup.group.length - 1];
                lastNodeLength = totalParallelCompsNodes[lastNode].nodeLength;
              }
              spacing = nodeLength >= lastNodeLength ? 1 - (nodeLength - lastNodeLength) : 1;
              nodeLength = lastNodeLength + spacing;
            }
          }
          prevParallelNode = { ...nodeItem, nodeLength: nodeLength }
          parallelCompsNodes[nodeItem.node] = { ...nodeItem, nodeLength, actualLength: nodeItem.nodeLength };
        }

        const sortParallelCompsNodes = Object.values(parallelCompsNodes).sort((a, b) => a.nodeLength - b.nodeLength);
        for (let index = 0; index < sortParallelCompsNodes.length; index++) {
          const item = sortParallelCompsNodes[index];
          const prevItem = sortParallelCompsNodes[index - 1];
          const nextItem = sortParallelCompsNodes[index + 1];
          if (index === 0) {
            if (nextItem && nextItem.actualLength - item.actualLength <= 1 && !item.groupNodes.includes(nextItem.node)) {
              currNodeGroup.push(item.node);
              currNodeGroupNodes[item.node] = [...item.groupNodes];
            } else {
              nodeGroups.push([item.node]);
            }
            continue;
          }

          if (item.actualLength - prevItem.actualLength <= 1 && !currNodeGroup.find(node => currNodeGroupNodes[node].includes(item.node))) {
            currNodeGroup.push(item.node);
            currNodeGroupNodes[item.node] = [...item.groupNodes];
          } else {
            nodeGroups.push([...currNodeGroup]);
            currNodeGroup = [];
            if (nextItem && nextItem.actualLength - item.actualLength <= 1 && !currNodeGroup.find(node => currNodeGroupNodes[node].includes(item.node))) {
              currNodeGroup.push(item.node);
              currNodeGroupNodes[item.node] = [...item.groupNodes];
            } else {
              nodeGroups.push([item.node]);
            }
          }
          if (index === sortParallelCompsNodes.length - 1) {
            nodeGroups.push([...currNodeGroup]);
          }
        }
        totalParallelCompsNodes = { ...totalParallelCompsNodes, ...parallelCompsNodes };
        for (let group of nodeGroups) {
          totalNodeGroups.push({ groupName, group });
        }
      }

      totalNodeGroups = totalNodeGroups.filter(group => group.group.length > 1).sort((a, b) => b.group.length - a.group.length);

      for (let groupName of lengthList) {
        const [start, end] = groupName.split('::');
        const filterPaths = this.signalGroupPath[root].filter(item => item.groupPath[0] === start && item.groupPath[item.groupPath.length - 1] === end);
        for (let path of filterPaths) {
          let prevSegment = null;
          for (let j = 1; j < path.groupPath.length - 1; j++) {

            const node = path.groupPath[j];
            const segment = path.segments.find(edge => edge.from === path.groupPath[j - 1] && edge.to === node) || {};
            if (!segment) {
              continue;
            }

            let nodeLength = 0
            const prevNode = path.groupPath[j - 1];
            let prevLength = allNodeLengthObj[prevNode] || 0;
            //find prev parallel group
            const findGroup = totalNodeGroups.find(group => group.group.includes(prevNode) && group.groupName === groupName);
            let groupSpacing = 0;
            if (findGroup) {
              const maxLength = findGroup.group.reduce((max, item) => Math.max(max, totalParallelCompsNodes[item].nodeLength), 0);
              const minLength = totalParallelCompsNodes[prevNode].nodeLength;
              groupSpacing = maxLength - minLength;
              if (groupSpacing < 0) {
                groupSpacing = 0
              }
            }

            let spacing = (totalParallelCompsNodes[node] || totalParallelCompsNodes[prevNode] || !this.compMap.get(node) || !this.compMap.get(prevNode)) ? 1 : singleLength;
            nodeLength = this.seriesRLCs.includes(prevNode) && prevSegment.endPin !== segment.startPin ? prevLength + spacing + 2 : prevLength + spacing;

            const findGroupIndex = totalNodeGroups.findIndex(group => group.group.includes(node) && group.groupName === groupName);

            if (totalParallelCompsNodes[node] && findGroupIndex > -1) {
              const findGroup = totalNodeGroups[findGroupIndex];
              const index = findGroup.group.findIndex(item => item === node);
              const firstNodeIndex = findGroup.group.findIndex(item => allNodeLengthObj[item]);

              if (index > -1) {
                if (firstNodeIndex < 0 || index === 0) {
                  if (firstNodeIndex < 0) {
                    totalNodeGroups[findGroupIndex].group.unshift(totalNodeGroups[findGroupIndex].group.splice(index, 1)[0]);
                  }
                  nodeLength = nodeLength + groupSpacing;
                  totalNodeGroups[findGroupIndex].startLength = nodeLength;
                } else {
                  const startLength = totalNodeGroups[findGroupIndex].startLength || 0;
                  const startSpacing = index - firstNodeIndex;
                  nodeLength = startLength + startSpacing;
                }
              }
            } else {
              nodeLength += groupSpacing;
            }
            if (!allNodeLengthObj[node]) {
              allNodeLengthObj[node] = nodeLength;
            }
            prevSegment = { ...segment };
          }
        }
      }

      for (let groupName of lengthList) {
        const [start, end] = groupName.split('::');
        const filterPaths = this.signalGroupPath[root].filter(item => item.groupPath[0] === start && item.groupPath[item.groupPath.length - 1] === end);
        let maxLength = 0, prevNodeLength = 0;
        for (let path of filterPaths) {
          let prevSegment = null;
          for (let j = 1; j < path.groupPath.length - 1; j++) {

            const node = path.groupPath[j];
            const segment = path.segments.find(edge => edge.from === path.groupPath[j - 1] && edge.to === node) || {};
            if (!segment) {
              continue;
            }

            let nodeLength = 0
            const prevNode = path.groupPath[j - 1];
            const nextNode = path.groupPath[j + 1];
            let prevLength = this.nodeLength[root].get(prevNode) || 0;
            //find prev parallel group
            const findGroup = totalNodeGroups.find(group => group.group.includes(prevNode) && group.groupName === groupName);
            let groupSpacing = 0;
            if (findGroup) {
              const maxLength = findGroup.group.reduce((max, item) => Math.max(max, totalParallelCompsNodes[item].nodeLength), 0);
              const minLength = totalParallelCompsNodes[prevNode].nodeLength;
              groupSpacing = maxLength - minLength;
              if (groupSpacing < 0) {
                groupSpacing = 0
              }
            }

            let spacing = (totalParallelCompsNodes[node] || totalParallelCompsNodes[prevNode] || !this.compMap.get(node) || !this.compMap.get(prevNode)) ? 1 : singleLength;
            nodeLength = this.seriesRLCs.includes(prevNode) && prevSegment.endPin !== segment.startPin ? prevLength + spacing + 2 : prevLength + spacing;

            if (this.seriesRLCs.includes(node)/*  || this.seriesRLCs.includes(prevNode) */) {
              nodeLength += 1.2;
            }

            const findGroupIndex = totalParallelCompsNodes[node] ? totalNodeGroups.findIndex(group => group.group.includes(node) && group.groupName === groupName) : -1;
            const findPGroup = findGroupIndex > -1 ? totalNodeGroups[findGroupIndex] : null;
            if (totalParallelCompsNodes[node] && findPGroup) {
              const index = findPGroup.group.findIndex(item => item === node);
              const firstNodeIndex = findPGroup.group.findIndex(item => this.nodeLength[root].has(item));
              if (index > -1) {
                if (firstNodeIndex < 0 || index === 0) {
                  if (firstNodeIndex < 0) {
                    totalNodeGroups[findGroupIndex].group.unshift(totalNodeGroups[findGroupIndex].group.splice(index, 1)[0]);
                  }
                  nodeLength = nodeLength + groupSpacing;
                  totalNodeGroups[findGroupIndex].startLength = nodeLength;
                } else {
                  const startLength = totalNodeGroups[findGroupIndex].startLength || 0;
                  if (index < firstNodeIndex) {
                    totalNodeGroups[findGroupIndex].group.push(totalNodeGroups[findGroupIndex].group.splice(index, 1)[0]);
                  }
                  const _index = totalNodeGroups[findGroupIndex].group.indexOf(node);
                  const startSpacing = _index - firstNodeIndex;
                  nodeLength = startLength + startSpacing;
                }
              }
            } else {
              nodeLength += groupSpacing;

              //find next parallel group
              const findNextGroup = totalNodeGroups.find(group => group.group.includes(nextNode) && group.groupName === groupName);
              if (findNextGroup && allNodeLengthObj[node] && parallelPrevNode[nextNode] === node) {
                const startNode = findNextGroup.group[0];
                const startNodePrevNode = parallelPrevNode[startNode];
                const prevNodeLength = allNodeLengthObj[startNodePrevNode] || 0;
                if (nodeLength < prevNodeLength) {
                  nodeLength = nodeLength + (prevNodeLength - nodeLength);
                }
              }
            }
            if (!this.nodeLength[root].has(node)) {
              this.nodeLength[root].set(node, nodeLength);
            }
            if (j === path.groupPath.length - 2) {
              const _prevNodeLength = this.nodeLength[root].get(node);
              const currNLength = _prevNodeLength > nodeLength ? _prevNodeLength : nodeLength;
              maxLength = this.seriesRLCs.includes(node) ? Math.max(maxLength, currNLength + 2) : Math.max(maxLength, currNLength);
              prevNodeLength = maxLength;
            }
            prevSegment = { ...segment };
          }
          const endNode = path.groupPath[path.groupPath.length - 1];
          if (!this.ICComps.includes(endNode) && !this.repeaterComps.includes(endNode)) {
            this.nodeLength[root].set(endNode, prevNodeLength + 3)
          }

        }
        let totalLength = maxLength + 3 < currentLength ? currentLength : maxLength + 3;
        this.groupLayoutLength[root].set(groupName, totalLength);
      }

      const layers = [...this.groupNodeLayer[root].keys()];
      const groupLengthList = [...this.groupLayoutLength[root].keys()]
      const maxLayer = layers.reduce((max, item) => Math.max(max, this.groupNodeLayer[root].get(item)), 0);
      let layerLengthObj = {}
      for (let layer = 0; layer <= maxLayer; layer++) {
        const layerNodes = layers.filter(item => this.groupNodeLayer[root].get(item) === layer);
        if (layer === 0) {
          layerLengthObj[layer] = 0;
          continue;
        }

        const maxGroupLength = groupLengthList.reduce((max, groupName) => {
          const [startNode, endNode] = groupName.split('::');
          if (layerNodes.includes(endNode)) {
            const length = this.groupLayoutLength[root].get(groupName);
            const spacing = this.groupNodeLayer[root].get(startNode) !== layer - 1 ? (layerLengthObj[layer - 1] || 0) + 4 : 0;
            return Math.max(max, length - spacing);
          }
          return max;
        }, 0);
        for (let groupName of groupLengthList) {
          const [startNode, endNode] = groupName.split('::');
          if (layerNodes.includes(endNode)) {
            const spacing = this.groupNodeLayer[root].get(startNode) !== layer - 1 ? (layerLengthObj[layer - 1] || 0) + 4 : 0;
            this.groupLayoutLength[root].set(groupName, rootNodes.includes(endNode) ? maxGroupLength + spacing + 1 : maxGroupLength + spacing);
          }
        }
        layerLengthObj[layer] = maxGroupLength + 1;
      }
    }
  }

  getNodeWidth = (node) => {
    if (this.parallelRLCs.includes(node) || this.parallelComps.includes(node)) {
      return 0;
    }
    if (this.compMap.has(node)) {
      return 3;
    }
    return 1;
  }

  // Decomposing individual signal paths
  decomposeSignal = (signal, paths) => {
    const graph = this.buildGraph(paths);
    const allPaths = [];
    let existPathsSet = new Set();

    // Get pins of all root components as starting points
    const rootPins = [];
    graph.forEach((_, pinKey) => {
      const [comp] = pinKey.split(':');
      if (this.data.root.includes(comp)) {
        rootPins.push({ root: comp, pin: pinKey });
      }
    });

    for (let { root, pin: startPin } of rootPins) {
      // Terminal node definition: pins with degree 1 and not the current start
      const terminalPins = [];
      graph.forEach((neighbors, pinKey) => {
        if (pinKey !== startPin && neighbors.size === 1) {
          terminalPins.push(pinKey);
        }
      });

      for (let endPin of terminalPins) {
        const pathsToTerminal = this.findAllPaths(startPin, endPin, graph);

        for (const pinPath of pathsToTerminal) {
          // Convert pin path to component path while preserving pin strictness
          const compPath = pinPath.map(p => p.split(':')[0]);
          // Filter out consecutive duplicate components (represents internal component traversal)
          let uniqueCompPath = compPath.filter((c, i) => i === 0 || c !== compPath[i - 1]);

          // If the path starts and ends at the same component (returns to the start),
          // but passes through an external component (e.g. U4000),
          // collapse that loop to [root, externalComp] to avoid treating the same net
          // as a separate return-to-root terminal.
          if (uniqueCompPath.length > 1 && uniqueCompPath[0] === uniqueCompPath[uniqueCompPath.length - 1]) {
            // Find the first component different from root (the first external component)
            const firstExternal = compPath.find(c => c !== uniqueCompPath[0]);
            if (firstExternal) {
              uniqueCompPath = [uniqueCompPath[0], firstExternal];
            }
          }
          const pathKey = pinPath.join('->');
          if (!existPathsSet.has(pathKey)) {
            allPaths.push({ root, path: uniqueCompPath, pinPath }); // Save original pinPath for later use
            existPathsSet.add(pathKey);
          }
        }
      }
    }

    // First filter out paths that are strict prefixes of another path for the same root
    // to avoid keeping duplicates such as U0600->R0837 (short) alongside
    // U0600->R0837->U4000 (long).
    const isStrictPrefix = (a, b) => {
      if (!Array.isArray(a) || !Array.isArray(b)) return false;
      if (a.length >= b.length) return false;
      for (let i = 0; i < a.length; i++) {
        if (a[i] !== b[i]) return false;
      }
      return true;
    };

    const filteredAllPaths = allPaths.filter(p =>
      !allPaths.some(q => q !== p && q.root === p.root && isStrictPrefix(p.path, q.path))
    );
    const existUnusedComps = [];
    const signalPaths = filteredAllPaths.map((pathItem, index) => {
      const segments = [];
      const { root, path, pinPath } = pathItem;
      const toNode = pathItem.path[pathItem.path.length - 1];
      const isNotIC = !this.ICComps.includes(toNode) && !this.repeaterComps.includes(toNode);
      if (isNotIC && existUnusedComps.includes(toNode)) {
        return null
      }
      existUnusedComps.push(toNode)
      let pathWeight = 0;
      if (!this.groupActualLength[root]) {
        this.groupActualLength[root] = new Map();
      }
      if (!this.endParallelComps[root]) {
        this.endParallelComps[root] = [];
      }
      if (!this.groupNodeLayer[root]) {
        this.groupNodeLayer[root] = new Map();
      }
      if (!this.splitChipComps[root]) {
        this.splitChipComps[root] = [];
      }
      if (!this.chipCompsPrevList[root]) {
        this.chipCompsPrevList[root] = new Map();
      }
      const { groupPath, groupNodes } = this.getGroupPath(path);

      // Calculate path weight using component path
      for (let i = 0; i < path.length; i++) {
        const comp = path[i];
        if (this.compWeights[comp] === undefined) this.compWeights[comp] = 1;
        pathWeight += this.compWeights[comp];
      }

      // Build segments strictly using pinPath
      for (let i = 0; i < pinPath.length - 1; i++) {
        const fromPin = pinPath[i];
        const toPin = pinPath[i + 1];
        const [fromComp, fromPinName] = fromPin.split(':');
        const [toComp, toPinName] = toPin.split(':');

        const segment = paths.find(s =>
          (s.start.name === fromComp && s.start.pin === fromPinName && s.end.name === toComp && s.end.pin === toPinName) ||
          (s.end.name === fromComp && s.end.pin === fromPinName && s.start.name === toComp && s.start.pin === toPinName)
        );

        if (segment) {
          let segmentItem = {
            from: segment.start.name,
            to: segment.end.name,
            length: Number(segment.length),
            net: segment.net,
            startPin: segment.start.pin,
            endPin: segment.end.pin,
            signal
          };
          if (segment.start.name === toComp && segment.start.pin === toPinName) {
            segmentItem.from = segment.end.name;
            segmentItem.to = segment.start.name;
            segmentItem.startPin = segment.end.pin;
            segmentItem.endPin = segment.start.pin;
          }
          segments.push(segmentItem);
          // Ensure component definitions include referenced pins — some inputs
          // may contain pin references that aren't present in the `comps` list.
          const ensurePin = (compName, pinName) => {
            if (!pinName) return;
            const comp = this.compMap.get(compName);
            if (!comp) return;
            comp.pins = comp.pins || [];
            const exists = comp.pins.find(p => p.pin === pinName);
            if (!exists) {
              // Add a minimal pin entry so downstream layout can locate it.
              const newPin = { pin: pinName, net: segment.net || '', signal };
              comp.pins.push(newPin);
              // Also update the original comps array entry if present
              const orig = this.comps.find(c => c.name === compName);
              if (orig) {
                orig.pins = orig.pins || [];
                orig.pins.push({ ...newPin });
              }
            }
          };
          ensurePin(segment.start.name, segment.start.pin);
          ensurePin(segment.end.name, segment.end.pin);
        }
      }

      const pathName = `${signal}${index}`;

      //find max weight path
      if (!this.rootPathObj[root] || this.rootPathObj[root].pathWeight < pathWeight) {
        this.rootPathObj[root] = {
          signal,
          name: pathName,
          path: [...path],
          pathWeight,
          segments,
          groupPath,
          groupNodes,
          ICCompLength: this.comps.filter(item => path.includes(item.name) && !RLC_TYPES.includes(item.type)).length
        };
      } else if (this.rootPathObj[root].pathWeight === pathWeight) {
        const ICCompLength = this.comps.filter(item => path.includes(item.name) && !RLC_TYPES.includes(item.type)).length;
        if (ICCompLength > this.rootPathObj[root].ICCompLength) {
          this.rootPathObj[root] = {
            signal,
            name: pathName,
            path: [...path],
            pathWeight,
            segments,
            groupPath,
            groupNodes,
            ICCompLength
          };
        }
      }

      for (let itemPath of groupPath) {
        const endNode = itemPath[itemPath.length - 1];
        const name = `${itemPath[0]}::${endNode}`;
        if (!this.groupActualLength[root].has(name)) {
          this.groupActualLength[root].set(name, { name, groupNodes, nodeLength: path.length });
        }
      }

      if (!this.signalGroupPath[root]) {
        this.signalGroupPath[root] = [...groupPath.map(item => ({ signal, groupPath: item, segments, name: pathName }))];
      } else {
        const newGroupPath = groupPath.filter(item => !this.signalGroupPath[root].find(group => _.isEqual(group.groupPath, item)));
        if (newGroupPath.length) {
          this.signalGroupPath[root].push(...newGroupPath.map(item => ({ signal, groupPath: item, segments, name: pathName })));
        }
      }

      if (this.parallelAllComps.includes(toNode)) {
        this.endParallelComps[root].push(toNode);
      }

      for (let i = 0; i < groupNodes.length; i++) {
        const prevLayer = this.groupNodeLayer[root].get(groupNodes[i]) || 0;
        this.groupNodeLayer[root].set(groupNodes[i], Math.max(prevLayer, i));
        const prevList = this.chipCompsPrevList[root].get(groupNodes[i]) || [];
        prevList.push(groupNodes[i - 1]);
        this.chipCompsPrevList[root].set(groupNodes[i], [...new Set(prevList)]);
        if (i > 0 && i < groupNodes.length - 1 && this.ICComps.includes(groupNodes[i])) {
          this.splitChipComps[root].push(groupNodes[i]);
        }
      }

      return {
        signal,
        name: pathName,
        nodes: path,
        segments,
        groupPath,
        groupNodes,
        pathWeight,
        root
      };
    }).filter(it => !!it);

    return signalPaths
  }

  getGroupPath = (pathNodes) => {
    let groupPath = [], currPath = [], groupNodes = [];
    for (let node of pathNodes) {
      if (this.repeaterComps.includes(node) || this.ICComps.includes(node)) {
        groupNodes.push(node)
        currPath.push(node);
        if (currPath.length > 1) {
          groupPath.push([...currPath]);
          currPath = [node];
        }
      } else {
        currPath.push(node);
      }
      if (node === pathNodes[pathNodes.length - 1] && currPath.length > 1) {
        groupPath.push([...currPath]);
      }
    }

    return { groupPath, groupNodes };
  }

  // build graph from paths
  buildGraph = (paths) => {
    const graph = new Map();

    const addEdge = (u, v) => {
      if (!graph.has(u)) graph.set(u, new Set());
      if (!graph.has(v)) graph.set(v, new Set());
      graph.get(u).add(v);
      graph.get(v).add(u);
    };

    // 1. Build external connections (wires)
    for (let segment of paths) {
      const start = `${segment.start.name}:${segment.start.pin}`;
      const end = `${segment.end.name}:${segment.end.pin}`;
      addEdge(start, end);
    }

    // 2. Build internal component connections (for series components and repeaters)
    const compPinsMap = new Map();
    for (let segment of paths) {
      const comps = [segment.start, segment.end];
      for (let c of comps) {
        if (!compPinsMap.has(c.name)) compPinsMap.set(c.name, new Set());
        compPinsMap.get(c.name).add(`${c.name}:${c.pin}`);
      }
    }

    compPinsMap.forEach((pins, compName) => {
      if (this.seriesRLCs.includes(compName) || this.repeaterComps.includes(compName)) {
        const pinsArr = Array.from(pins);
        for (let i = 0; i < pinsArr.length; i++) {
          for (let j = i + 1; j < pinsArr.length; j++) {
            addEdge(pinsArr[i], pinsArr[j]);
          }
        }
      }
    });

    return graph;
  }

  // find terminal pins in comp connection graph
  findTerminalNodes = (graph, rootComps) => {
    const terminalNodes = [];
    graph.forEach((neighbors, pinKey) => {
      const [comp] = pinKey.split(':');
      // Terminal node: degree 1 and not on any root component
      if (neighbors.size === 1 && !rootComps.includes(comp)) {
        terminalNodes.push(pinKey);
      }
    });

    return terminalNodes;
  }
  // Find all paths between two points
  findAllPaths = (start, end, graph, visited = new Set(), currentPath = []) => {
    visited.add(start);
    currentPath.push(start);
    const paths = [];

    if (start === end) {
      paths.push([...currentPath]);
    } else {
      const neighbors = graph.get(start) || new Set();
      for (const neighbor of neighbors) {
        if (!visited.has(neighbor)) {
          // Strictness check: if entering a pin of a non-series/non-repeater component,
          // do not exit through other pins of that component unless that pin is the terminal
          const [currComp] = start.split(':');
          const [nextComp] = neighbor.split(':');

          // Allow traversal across different pins of the same component when the edge exists
          // in the graph (e.g. U1000:C1 -> U1000:F1).
          // Previously we prevented internal traversal for known components, which caused
          // explicit pin-to-pin edges in the input to be ignored. Here we preserve the
          // graph's edges and do not globally block movement within the same component.

          const newPaths = this.findAllPaths(neighbor, end, graph, visited, currentPath);
          paths.push(...newPaths);
        }
      }
    }

    visited.delete(start);
    currentPath.pop();
    return paths;
  }

  // find connection segment between two nodes in comp connection graph
  findSegment = (paths, from, to, type) => {
    if (!Array.isArray(paths)) return type === "find" ? null : [];
    if (type === "find") {
      return paths.find(segment =>
        segment && segment.start && segment.end &&
        ((segment.start.name === from && segment.end.name === to) ||
          (segment.start.name === to && segment.end.name === from))
      );
    }
    return paths.filter(segment =>
      segment && segment.start && segment.end &&
      ((segment.start.name === from && segment.end.name === to) ||
        (segment.start.name === to && segment.end.name === from))
    );
  }

  getResults() {
    return this.decomposedPaths;
  }
}

export default PathDecomposer;
